import React from 'react';
import { useProjectDetailsContext } from '../contexts/ProjectDetailsContext';
import { PackCopy } from '../types';
import { useNavigateWithinProject } from '../utils/navigation';
import { formatDate } from '../utils/timeUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCopy,
    faEllipsisV,
    faTrash,
} from '@fortawesome/free-solid-svg-icons';
import Avatar from './Avatar';
import LanguageTag from './LanguageTag';
import EllipsisOptions from './EllipsisOptions';

const PackCopyOverview: React.FC = () => {
    const {
        state: { packCopies, userDetailsMapping },
        handlers: { handlePackCopyDuplicate, handlePackCopyDelete },
    } = useProjectDetailsContext();

    const navigate = useNavigateWithinProject();

    const handleSelect = (packCopy: PackCopy) => {
        // Handle selection logic here
        navigate(`/pack-copies/${packCopy.id}`);
    };

    return (
        <div className="centred-max-width-1100">
            <h2>Pack Copies</h2>
            {packCopies.length > 0 ? (
                <div className="table-container margin-top-16">
                    <table className="base-table">
                        <thead>
                            <tr>
                                <th>Version Name</th>
                                <th>Notes</th>
                                <th>Languages</th>
                                <th>Created By</th>
                                <th>Date</th>
                                <th></th> {/* Empty header for action button */}
                            </tr>
                        </thead>
                        <tbody>
                            {packCopies.map((packCopy) => {
                                const userDetails =
                                    userDetailsMapping[
                                        packCopy.creation.user_id
                                    ];
                                const userName = userDetails
                                    ? `${userDetails.first_name} ${userDetails.last_name}`
                                    : 'Unknown User';
                                const userEmail = userDetails
                                    ? userDetails.email_address
                                    : 'Unknown Email';
                                return (
                                    <tr
                                        key={packCopy.id}
                                        onClick={() => handleSelect(packCopy)}
                                        className="pack-copy-row"
                                    >
                                        <td className="name-cell">
                                            {packCopy.name}
                                        </td>
                                        <td className="notes-cell">
                                            {packCopy.notes}
                                        </td>
                                        <td>
                                            <div className="language-tags">
                                                {packCopy.languages?.map(
                                                    (language, index) => (
                                                        <LanguageTag
                                                            key={index}
                                                            language={language}
                                                        />
                                                    ),
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="user-details-container">
                                                <Avatar
                                                    userDetails={
                                                        userDetailsMapping[
                                                            packCopy.creation
                                                                .user_id
                                                        ]
                                                    }
                                                    showTooltip={false}
                                                    height={32}
                                                />
                                                <div className="text">
                                                    <p className="user-name">
                                                        {userName}
                                                    </p>
                                                    <p className="user-email">
                                                        {userEmail}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            {formatDate(
                                                packCopy.creation.timestamp,
                                            )}
                                        </td>
                                        <td>
                                            {/* <button className="ellipsis-button">
                                                <FontAwesomeIcon
                                                    icon={faEllipsisV}
                                                />
                                            </button> */}
                                            <EllipsisOptions
                                                options={[
                                                    {
                                                        icon: faCopy,
                                                        label: 'Duplicate',
                                                        onClick: () =>
                                                            handlePackCopyDuplicate(
                                                                packCopy.id,
                                                            ),
                                                    },
                                                    {
                                                        icon: faTrash,
                                                        label: 'Delete',
                                                        onClick: () =>
                                                            handlePackCopyDelete(
                                                                packCopy.id,
                                                            ),
                                                        variant: 'danger',
                                                    },
                                                ]}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className="no-results-message">
                    No pack copies uploaded yet.
                </p>
            )}
        </div>
    );
};

export default PackCopyOverview;
